/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import { useState, useMemo } from "react";
import { useNavigate } from 'react-router';
import { PersonIcon } from "@/shared/ui";
import { useTranslation } from "react-i18next";
import { ClientCrudProvider, type ClientRow } from "../context/ClientManagementContext";
import { ClientDrawer } from "../components/ClientDrawer";
import { buildEditDefaultValues } from "./client-detail-mappers";
import { useAgency } from "@pages/authenticated/agency-pages/AgenciesContext";
import {
  DataState,
  DataStateError,
  TableRowsSkeleton,
  useDataStateOverride,
  useFirstLoadGate,
  applyDataStateOverride,
} from "@/shared/ui";
import { ClientsTableView } from "../components/ClientsTable/ClientsTableView";
import { ClientsMobileCardView } from '@/features/client-management/components/ClientsTable/ClientsMobileCardView';
import { ClientListHeader } from "./ManageClientsPage/ClientListHeader";
import { useClientList } from "./ManageClientsPage/useClientList";
import { useIsMobile } from "@/shared/hooks/use-is-mobile";
import { getListingPageLayout } from "@/shared/ui/layout/listing-page-layout";
import { FF_APP_SPREADSHEETS } from "@/shared/utils/feature-flag";
import {
  ImportResultBanner,
  SpreadsheetMenu,
  useImportResultBanner,
} from "@features/spreadsheets";

const SK = 'animate-pulse rounded-(--radius-md) bg-[var(--skeleton-base)]';

/**
 * The columns an export of this list carries, matching what the table shows.
 * Sent with the export so the file mirrors the screen it was asked for.
 */
const CLIENT_EXPORT_COLUMNS = [
  'full_name', 'type_of_client', 'phone', 'email', 'last_contact_date',
];

/**
 * Loading placeholder for the client list header.
 */
function ClientHeaderSkeleton() {
  return (
    <div
      style={{
        padding: 'var(--space-4) var(--space-5)',
        borderRadius: 'var(--radius-lg)',
        border: '1px solid var(--border-subtle)',
        background: 'var(--surface-primary)',
        boxShadow: 'var(--shadow-md)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--space-3)',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div className={`${SK} h-7 w-36`} />
        <div className={`${SK} h-9 w-32`} />
      </div>
      <div style={{ display: 'flex', gap: 'var(--space-2)' }}>
        <div className={`${SK} h-10 flex-1`} />
        <div className={`${SK} h-10 w-24`} />
      </div>
    </div>
  );
}

/**
 * Manage-clients content: search, filters, list, and add-client flow.
 *
 * @returns The rendered manage-clients content.
 */
function ManageClientsContent() {
  const { t } = useTranslation('features/clients');
  const navigate = useNavigate();
  const dataStateOverride = useDataStateOverride();
  const [showForm, setShowForm] = useState(false);
  const [editingClient, setEditingClient] = useState<ClientRow | null>(null);
  const isMobile = useIsMobile();

  const { agency } = useAgency((state) => state);
  const agency_id = agency.id;
  const brokers = useMemo(
    () => (agency.members || []).map((m) => ({ id: m.id, name: `${m.first_name} ${m.last_name}`.trim() })),
    [agency.members],
  );

  const {
    rows, isLoading, isLoadingMore, loadError, totalCount, fetchClients,
    filters, setFilters,
    sentinelRef, scrollContainerRef,
  } = useClientList(agency_id);

  const importBanner = useImportResultBanner({
    agencyId: agency_id ?? '',
    importFilter: filters.importId ?? '',
    setImportFilter: (importId) => setFilters({ importId }),
    onRefresh: fetchClients,
  });

  const effectiveRows = dataStateOverride === 'empty' ? [] : rows;
  // Gate the full-surface skeleton on the FIRST load only. A search keystroke
  // debounces into a filter change that refetches (isLoading flips true
  // again); driving the header's mount off raw isLoading unmounted it on every
  // keystroke, destroying the search input's locally-held text and focus (the
  // reported bug). The properties list relies on this same first-load split.
  const { showLoadingSkeleton } = useFirstLoadGate(isLoading);
  const clientDataStateFlags = applyDataStateOverride(dataStateOverride, {
    isLoading: showLoadingSkeleton,
    isError: loadError,
    isEmpty: rows.length === 0,
  });

  const hasReachedEnd = !isLoading && !isLoadingMore
    && rows.length > 0 && totalCount != null && rows.length >= totalCount;

  const handleFormClose = () => { setShowForm(false); setEditingClient(null); };
  const handleFormSuccess = () => { handleFormClose(); fetchClients(); };
  const startCreate = () => { setEditingClient(null); setShowForm(true); };
  const startEdit = (client: ClientRow) => { setEditingClient(client); setShowForm(true); };

  const handleRowNavigate = (clientId: string) => {
    navigate(clientId);
  };

  // Same wrapper stack the property list uses, so the control bar lands at
  // the same x/y with the same width on both screens.
  const { rootStyle, paddingStyle, stackStyle, scrollContainerClassName } = getListingPageLayout(isMobile);

  return (
    <div style={{ height: '100%', fontFamily: 'var(--font-family)' }}>
      <div style={rootStyle}>
        <div style={paddingStyle}>
          <div style={stackStyle}>
            {/* The list stays mounted while the drawer is open: the drawer is an
                overlay, so unmounting the page behind it left the dimmed
                backdrop covering an empty screen instead of the list. */}
            <>
              {!clientDataStateFlags.isError && !clientDataStateFlags.isLoading && (
                <ClientListHeader
                  t={t}
                  filters={filters}
                  setFilters={setFilters}
                  setSearch={(v) => setFilters({ q: v })}
                  onAddClient={startCreate}
                  brokers={brokers}
                  secondaryAction={FF_APP_SPREADSHEETS ? (
                    <SpreadsheetMenu
                      entity="clients"
                      agencyId={agency_id ?? ''}
                      totalCount={totalCount ?? rows.length}
                      filters={filters}
                      columns={CLIENT_EXPORT_COLUMNS}
                    />
                  ) : undefined}
                />
                )}

                {FF_APP_SPREADSHEETS && importBanner.summary && (
                  <ImportResultBanner
                    entity="clients"
                    summary={importBanner.summary}
                    onlyThisImport={importBanner.onlyThisImport}
                    onToggleOnlyThisImport={importBanner.toggleOnlyThisImport}
                    onUndo={importBanner.undo}
                    onDismiss={importBanner.dismiss}
                  />
                )}

                <DataState
                isLoading={clientDataStateFlags.isLoading}
                isError={clientDataStateFlags.isError}
                isEmpty={false}
                onRetry={() => { fetchClients(); }}
                errorMessage={t('list.loadError.message')}
                errorState={
                  <DataStateError
                    icon={<PersonIcon width={28} height={28}
                      style={{ color: 'var(--text-tertiary)', opacity: 0.5 }}
                    />}
                    message={t('list.loadError.message')}
                    retryLabel={t('list.loadError.retry')}
                    onRetry={() => { fetchClients(); }}
                  />
                }
                loadingFallback={<><ClientHeaderSkeleton /><div style={{ marginTop: 'var(--space-4)' }}><TableRowsSkeleton rows={6} /></div></>}
                >
                <div ref={scrollContainerRef} className={scrollContainerClassName}>
                  {totalCount != null && (
                    <div style={{ padding: 'var(--space-1) var(--space-1) var(--space-2)', color: 'var(--text-tertiary)', fontSize: 'var(--text-meta)' }}>
                      {t('list.resultsCount', { count: totalCount })}
                    </div>
                  )}
                  {/*
                    * Six columns do not fit a phone. The card view carries the
                    * three facts that decide whether you tap a row, and puts
                    * WhatsApp within thumb reach; the table stays untouched on
                    * desktop, where the columns are what people scan.
                    */}
                  {isMobile ? (
                    <ClientsMobileCardView
                      clients={effectiveRows}
                      hasActiveQuery={Boolean(filters.q || filters.type || filters.status)}
                      onRowNavigate={handleRowNavigate}
                      onAddClient={startCreate}
                      t={t}
                    />
                  ) : (
                    <ClientsTableView
                      clients={effectiveRows}
                      hasActiveQuery={Boolean(filters.q || filters.type || filters.status)}
                      onRowNavigate={handleRowNavigate}
                      onAddClient={startCreate}
                      onEditClient={startEdit}
                      t={t}
                    />
                  )}
                  {rows.length > 0 && (
                    <>
                      {isLoadingMore && (
                        <div style={{ padding: 'var(--space-4)', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-meta)' }}>
                          {t('list.loadingMore')}
                        </div>
                      )}
                      {!isLoadingMore && hasReachedEnd && (
                        <div style={{ padding: 'var(--space-4)', textAlign: 'center', color: 'var(--text-tertiary)', fontSize: 'var(--text-meta)' }}>
                          {t('list.endOfList')}
                        </div>
                      )}
                      <div ref={sentinelRef} style={{ height: 1 }} />
                    </>
                  )}
                </div>
                </DataState>
            </>
          </div>
        </div>
      </div>

      <ClientDrawer
        // Remount when switching between create and a different client so the
        // drawer's edit defaults are rebuilt from the newly selected row.
        key={editingClient?.client_id ?? 'create'}
        open={showForm}
        onClose={handleFormClose}
        mode={editingClient ? 'edit' : 'create'}
        clientId={editingClient?.client_id}
        defaultValues={editingClient ? buildEditDefaultValues(editingClient) : undefined}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
}

export default function ManageClientsPage() {
  return (
    <ClientCrudProvider>
      <ManageClientsContent />
    </ClientCrudProvider>
  );
}
